import { createContext, useContext, useState, type ReactNode } from 'react';
import { translations } from './i18n';

export const ROUTE_PATHS = {
  HOME: '/',
  ABOUT: '/about',
  SERVICES: '/services',
  ADVISORY: '/advisory-board',
  NEWS: '/company-news',
  CONTACT: '/contact',
  PRIVACY: '/privacy-policy',
  TERMS: '/terms-of-service',
  COMPLIANCE: '/compliance',
} as const;

export interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  features: string[];
  benefits: string[];
}

export interface TeamMember {
  id: string;
  name: string;
  nameEn?: string;
  position: string;
  bio: string;
  avatar: string;
  expertise: string[];
}

export interface Stat {
  label: string;
  value: string;
  icon: string;
}

export type Language = 'zh-CN' | 'zh-TW' | 'en' | 'ja' | 'ko';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-unknown
  t: (typeof translations)[Language];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language] as (typeof translations)[Language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
